import { useState } from 'react'
import { EventCode, trackInternalEvent } from '@rio/tracking'
import { useI18n } from 'builder/hooks/useI18n'
import { usePostRedirectUrl } from '../hooks/useGetRedirectUrl'

import { TRANSLATION } from '../utils'
import { RawCourse, CourseProgressStatus, CourseProgressResponse } from '../types'
import { CourseCard } from '../CourseCard'

import Carousel from '../Carousel'
import * as Styles from './styles'
import { Empty } from './Empty'

type CourseProgressTab = {
  id: CourseProgressStatus
  label: string
  eventId?: string
}

export const CoursesProgress = (props: { data: CourseProgressResponse }) => {
  const { i18n } = useI18n()
  const { mutateAndRedirect } = usePostRedirectUrl()

  const KEY = `${TRANSLATION}.progress`
  const TABS: CourseProgressTab[] = [
    {
      id: CourseProgressStatus.IN_PROGRESS,
      label: i18n.t(`${KEY}.tab.in_progress`),
      eventId: 'click_my_courses_tab',
    },
    {
      id: CourseProgressStatus.COMPLETED,
      label: i18n.t(`${KEY}.tab.completed`),
      eventId: 'click_my_courses_tab',
    },
    {
      id: CourseProgressStatus.SAVED,
      label: i18n.t(`${KEY}.tab.saved`),
      eventId: 'click_my_courses_tab',
    },
  ]
  const [selectedTab, setSelectedTab] = useState<CourseProgressStatus>(TABS[0].id)
  const selectedTabData = props.data?.[selectedTab] || []

  const handleChangeTab = (tab: CourseProgressTab) => {
    if (tab.eventId) {
      trackInternalEvent(tab.eventId as EventCode, { tab: tab.id })
    }
    setSelectedTab(tab.id)
  }

  const handleClick = async (cmsgid: string) => mutateAndRedirect({ cmsgid })

  return (
    <Styles.Container data-testid="courses-progress">
      <Styles.HeaderContainer>
        <Styles.Header>{i18n.t(`${KEY}.title`)}</Styles.Header>
        <Styles.TabsContainer data-testid="progress-tabs">
          {TABS.map(tab => (
            <Styles.StyledTab
              key={tab.id}
              isActive={selectedTab === tab.id}
              onClick={() => handleChangeTab(tab)}
              data-testid={`${tab.id}-tab`}
            >
              {tab.label}
            </Styles.StyledTab>
          ))}
        </Styles.TabsContainer>
      </Styles.HeaderContainer>
      <Styles.CardsContainer data-testid={`${selectedTab}-courses`}>
        {!selectedTabData.length ? (
          <Empty state={selectedTab} />
        ) : (
          <Carousel>
            {selectedTabData.map((course: RawCourse, idx: number) => (
              <CourseCard
                key={course.course_uuid || idx}
                courseId={course.cmsgid}
                onClick={() => handleClick(course.cmsgid)}
                title={course.title}
                description={course.introduction || ''}
                imageSrc={course.small_image}
                rating={course?.progress?.steps_completed_ratio ?? undefined}
                weeks={course.duration_in_weeks}
                hoursPerWeek={course.hours_per_week}
                enrolled={course.open_for_enrolment}
                provider={course.organisation?.name}
                status="progress"
                category={course.categories?.[0]}
                logoSrc={course.organisation?.organisation_image}
                progress={
                  course?.progress
                    ? Math.round(course.progress.steps_completed_ratio * 100)
                    : undefined
                }
              />
            ))}
          </Carousel>
        )}
      </Styles.CardsContainer>
    </Styles.Container>
  )
}
